import type { Account } from "@/api/types";

/**
 * Category tone + vendor initials for the activity feed.
 *
 * Replaces the emoji-per-category map this app shipped first. The reasons that
 * map failed are worth keeping written down, because both are easy to re-invent:
 *
 *  1. Platform drift. An emoji is a glyph the OS picks. The booth laptop and the
 *     visitor's phone are guaranteed to be different platforms — that IS the
 *     demo — so a feed keyed on emoji is guaranteed to look like two different
 *     products on the two screens standing next to each other.
 *
 *  2. Coverage. The fallback caught about a third of the Schedule F lines, so a
 *     vet bill, a fertilizer purchase and a receipt row all rendered as the same
 *     clipboard. That reads as unfinished software on the one screen a prospect
 *     is studying.
 *
 * Vendor initials in a category-tinted tile solve both: the initials are always
 * right because they come from the vendor's own name, and the tone degrades
 * gracefully — an unmatched category gets a neutral tile rather than a wrong
 * picture. It is also the pattern every finance app a farmer already uses has
 * trained them to read.
 *
 * COLOUR IS NOT THE ONLY CHANNEL. The category name is printed on the row
 * directly under the vendor name; the tone is reinforcement, never the message.
 */

/** Keys map to the --tone-* custom properties in styles/tokens.css. */
export type CategoryTone =
  | "inputs"
  | "fuel"
  | "machine"
  | "animal"
  | "utility"
  | "freight"
  | "neutral";

/**
 * Ordered — FIRST match wins, so specific patterns must precede general ones.
 * "Veterinary, Breeding and Medicine" has to be caught before /feed/, or a vet
 * bill is toned as livestock.
 *
 * Every one of the 19 seeded Schedule F expense lines is covered deliberately.
 */
const TONE_PATTERNS: Array<[RegExp, CategoryTone]> = [
  // Specific first.
  [/veterinar|breeding|medicine/i, "animal"],
  [/fertiliz|lime/i, "inputs"],
  [/seeds?\b|plants/i, "inputs"],
  [/chemical/i, "inputs"],
  [/gasoline|fuel|oil/i, "fuel"],
  [/car and truck|vehicle/i, "fuel"],
  [/freight|trucking/i, "freight"],
  [/storage|warehous/i, "freight"],
  [/repair|maint|parts/i, "machine"],
  [/rent or lease|lease|equipment/i, "machine"],
  [/custom hire|custom work/i, "machine"],
  [/labor|payroll|wage|hired/i, "utility"],
  [/insurance/i, "utility"],
  [/interest/i, "utility"],
  [/tax/i, "utility"],
  [/utilit/i, "utility"],
  [/suppl/i, "inputs"],
  // General last.
  [/feed|livestock|dairy|cattle/i, "animal"],
  [/grain|crop|harvest/i, "inputs"],
];

export function toneForAccount(account: Account | null | undefined): CategoryTone {
  if (!account) return "neutral";
  for (const [pattern, tone] of TONE_PATTERNS) {
    if (pattern.test(account.name)) return tone;
  }
  return "neutral";
}

/**
 * Up to two initials from a vendor name.
 *
 * Strips anything that is not a letter or space FIRST, so "Gwinner Feed & Seed"
 * gives GF rather than G& — an ampersand is not an initial, and a tile reading
 * "G&" is the kind of small wrongness that makes software look unfinished.
 * Falls back to "?" so a blank tile is impossible.
 */
export function initialsForVendor(name: string): string {
  const initials = name
    .replace(/[^A-Za-z ]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  return initials || "?";
}
